import React, { useState } from 'react';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { calculateYTD, calculateYTG, calculateYtdYtg, copySummaryTableToClipboard, getOrderedMonthNames, isForecastInPast, isPeriodInActualsWindow } from '../Utilities/tableUtils';
import { findComparisonPeriodIndex } from '../Utilities/monthLabelUtils';
import { formatWeekHeader, isDatePeriod } from '../Utilities/weekNumberUtils';
import IconButton from '@mui/material/IconButton';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import UnfoldMoreIcon from '@mui/icons-material/UnfoldMore';
import UnfoldLessIcon from '@mui/icons-material/UnfoldLess';
import FullscreenIcon from '@mui/icons-material/Fullscreen';
import FullscreenExitIcon from '@mui/icons-material/FullscreenExit';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import Tooltip from '@mui/material/Tooltip';
import './SkuBaseForecastTable.css';

const channelIdentifier = (channel) => channel?.hierarchyIdentifier ?? (channel?.channelId != null
    ? String(channel.channelId)
    : `path:${channel?.path ?? channel?.name ?? 'Uncategorised'}`);
const customerIdentifier = (customer) => String(customer?.customerId ?? customer?.name ?? '');
const skuIdentifier = (sku) => String(sku?.productId ?? sku?.skuNumber ?? '');

const flattenAllChannels = (channels = [], depth = 0) => channels.flatMap(channel => [
    { ...channel, hierarchyDepth: depth },
    ...flattenAllChannels(channel.children || [], depth + 1)
]);

const withStableHierarchyIdentities = (channel, depth) => ({
    ...channel,
    hierarchyIdentifier: channelIdentifier(channel),
    name: channelIdentifier(channel),
    displayName: channel.name,
    hierarchyDepth: depth,
    customers: (channel.customers || []).map(customer => ({
        ...customer,
        name: customerIdentifier(customer),
        displayName: customer.name,
        skus: (customer.skus || []).map(sku => ({
            ...sku,
            skuNumber: skuIdentifier(sku),
            displaySkuNumber: sku.skuNumber
        }))
    }))
});

const flattenVisibleChannels = (channels = [], expandedRows = new Set(), depth = 0) =>
    channels.flatMap(channel => {
        const row = withStableHierarchyIdentities(channel, depth);
        return expandedRows.has(channelIdentifier(channel))
            ? [row, ...flattenVisibleChannels(channel.children || [], expandedRows, depth + 1)]
            : [row];
    });

const findChannel = (channels, identifier) =>
    flattenAllChannels(channels).find(channel => channelIdentifier(channel) === String(identifier)) || null;

const OutputsSummaryTable = ({ summaryData, view, startMonth, selectedForecast, aggregationPeriod = "monthly", showActuals = false, loadingActuals = false, showCompare = false, groupBy: groupByProp = "channel", comparisonData = null, ytgSourceData = null, comparisonYtgSourceData = null, comparisonForecast = null, loadingComparison = false, isComparingForecasts = false, onToggleFullscreen, isFullscreen = false }) => {
    const [expandedRows, setExpandedRows] = useState(new Set());
    const [expandedCustomers, setExpandedCustomers] = useState(new Set());
    const [stickyColumnsEnabled, setStickyColumnsEnabled] = useState(true);
    const groupBy = groupByProp === 'channel' ? 'list' : groupByProp;
    const allSummaryChannels = flattenAllChannels(summaryData?.channels || []);
    const visibleSummaryChannels = flattenVisibleChannels(summaryData?.channels || [], expandedRows);

    const currentYear = new Date().getFullYear();
    const currentCalendarMonth = new Date().getMonth() + 1; // 1-12
    const selectedYear = selectedForecast?.startDate ? new Date(selectedForecast.startDate).getFullYear() : currentYear;
    const comparisonYear = comparisonForecast?.startDate ? new Date(comparisonForecast.startDate).getFullYear() : selectedYear;
    const totalPeriods = view === 'overlay'
        ? (aggregationPeriod === "monthly" ? 12 : (aggregationPeriod === "quarterly" ? 4 : (summaryData?.totals?.grossRev?.length - 1 || 12)))
        : (aggregationPeriod === "monthly" ? 12 : (summaryData?.totals?.[view]?.length - 1 || 12));
    
    const showYTG = !isForecastInPast(selectedYear, currentYear, summaryData?.currentMonth, totalPeriods, startMonth, currentCalendarMonth);
    const comparisonShowYTG = !isForecastInPast(comparisonYear, currentYear, comparisonData?.currentMonth, totalPeriods, startMonth, currentCalendarMonth);

    // Use period names from API response if available, otherwise fall back to month names
    const rawPeriodNames = summaryData?.periodNames || getOrderedMonthNames(startMonth);
    // Build period names array: always filter out YTG/FY, then add them back
    // YTG is conditionally included based on showYTG to match header rendering
    let basePeriodNames = [...(rawPeriodNames.filter(name => name !== 'YTD' && name !== 'YTG' && name !== 'FY'))];
    if (showYTG) {
        basePeriodNames.push('YTD');
        basePeriodNames.push('YTG');
    }
    basePeriodNames.push('FY');
    
    // Format period names with week numbers if it's weekly aggregation
    const periodNames = aggregationPeriod === "weekly" 
        ? basePeriodNames.map((period, index) => {
            // Check if this looks like a date (DD-MM-YYYY or DD-MM-YY format) 
            if (/^\d{2}-\d{2}-\d{2,4}$/.test(period)) {
                return formatWeekHeader(period, index);
            }
            return period; // Keep YTD, YTG, FY, etc. as is
        })
        : basePeriodNames;
    const ytgCurrentPeriod = ytgSourceData?.currentMonth ?? summaryData?.currentMonth;
    const ytgPeriodNames = ytgSourceData?.periodNames || rawPeriodNames;
    const ytgAggregationPeriod = ytgSourceData && aggregationPeriod !== 'weekly' ? 'weekly' : aggregationPeriod;
    const comparisonYtgCurrentPeriod = comparisonYtgSourceData?.currentMonth ?? comparisonData?.currentMonth;
    const comparisonYtgPeriodNames = comparisonYtgSourceData?.periodNames || comparisonData?.periodNames || rawPeriodNames;
    const comparisonYtgAggregationPeriod = comparisonYtgSourceData && aggregationPeriod !== 'weekly' ? 'weekly' : aggregationPeriod;
    const currencyPrefix = ['grossRev', 'overlay', 'netRev', 'fixedSpend', 'grossProfit'].includes(view) ? '£' : '';
    const percentPostfix = view === 'grossProfitPercent' ? '%' : '';

    const formatValue = (value) => {
        if (value === undefined || value === null) return 'N/A';
        const roundedValue = Math.round(value * 100) / 100;
        const displayValue = roundedValue % 1 === 0 ? roundedValue.toString() : roundedValue.toFixed(2);
        return `${currencyPrefix}${displayValue.replace(/\B(?=(\d{3})+(?!\d))/g, ",")}${percentPostfix}`;
    };

    const buildGrossProfitPercentSeries = (entity, currentPeriod, forecastYear = selectedYear) => {
        const grossProfitSeries = entity?.grossProfit;
        const netRevenueSeries = entity?.netRev;

        if (!Array.isArray(grossProfitSeries) || !Array.isArray(netRevenueSeries) || currentPeriod === undefined || currentPeriod === null) {
            return null;
        }

        const forecastHasNotStarted = forecastYear !== undefined
            && currentYear !== undefined
            && (forecastYear > currentYear || (forecastYear === currentYear && currentCalendarMonth < startMonth));

        const boundaryIndex = forecastHasNotStarted
            ? 0
            : Math.max(0, Math.min(Number(currentPeriod), Math.max(grossProfitSeries.length, netRevenueSeries.length) - 1));
        const useActuals = showActuals && Array.isArray(entity?.actualGrossProfit) && Array.isArray(entity?.actualNetRev);

        const blendedGrossProfitSeries = grossProfitSeries.map((forecastValue, idx) => {
            if (!useActuals) {
                return forecastValue ?? 0;
            }

            return idx < boundaryIndex
                ? entity.actualGrossProfit[idx] ?? 0
                : forecastValue ?? 0;
        });

        const blendedNetRevenueSeries = netRevenueSeries.map((forecastValue, idx) => {
            if (!useActuals) {
                return forecastValue ?? 0;
            }

            return idx < boundaryIndex
                ? entity.actualNetRev[idx] ?? 0
                : forecastValue ?? 0;
        });

        return {
            boundaryIndex,
            blendedGrossProfitSeries,
            blendedNetRevenueSeries,
            useActuals
        };
    };

    const calculateGrossProfitPercentYtgValue = (entity, currentPeriod, aggregationPeriodOverride = ytgAggregationPeriod, forecastYear = selectedYear) => {
        const series = buildGrossProfitPercentSeries(entity, currentPeriod, forecastYear);
        if (!series) {
            return { value: 0, state: 'forecast' };
        }

        const fyIndex = Math.max(0, series.blendedGrossProfitSeries.length - 1);
        const startIndex = Math.max(0, Math.min(series.boundaryIndex, fyIndex));
        const ytgGrossProfit = series.blendedGrossProfitSeries.slice(startIndex, fyIndex).reduce((sum, value) => sum + (value ?? 0), 0);
        const ytgNetRevenue = series.blendedNetRevenueSeries.slice(startIndex, fyIndex).reduce((sum, value) => sum + (value ?? 0), 0);
        const value = ytgNetRevenue === 0 ? 0 : Math.round((ytgGrossProfit / ytgNetRevenue) * 100 * 100) / 100;

        if (!series.useActuals) {
            return { value, state: 'forecast' };
        }

        const actualGrossProfitSeries = entity?.actualGrossProfit;
        const range = Array.from({ length: Math.max(0, fyIndex - startIndex) }, (_, offset) => startIndex + offset);
        const hasAnyActual = range.some(idx => actualGrossProfitSeries?.[idx] !== null && actualGrossProfitSeries?.[idx] !== undefined);
        const hasAnyForecastFallback = range.some(idx =>
            (actualGrossProfitSeries?.[idx] === null || actualGrossProfitSeries?.[idx] === undefined) &&
            series.blendedGrossProfitSeries[idx] !== null &&
            series.blendedGrossProfitSeries[idx] !== undefined);

        if (hasAnyActual && hasAnyForecastFallback) {
            return { value, state: aggregationPeriodOverride === 'weekly' ? 'actual' : 'mixed' };
        }

        if (hasAnyActual) {
            return { value, state: 'actual' };
        }

        return { value, state: 'forecast' };
    };

    const calculateGrossProfitPercentYtdValue = (entity, currentPeriod, forecastYear = selectedYear) => {
        const series = buildGrossProfitPercentSeries(entity, currentPeriod, forecastYear);
        if (!series) {
            return null;
        }

        const ytdGrossProfit = series.blendedGrossProfitSeries.slice(0, series.boundaryIndex).reduce((sum, value) => sum + (value ?? 0), 0);
        const ytdNetRevenue = series.blendedNetRevenueSeries.slice(0, series.boundaryIndex).reduce((sum, value) => sum + (value ?? 0), 0);

        if (ytdNetRevenue === 0) {
            return 0;
        }

        return Math.round((ytdGrossProfit / ytdNetRevenue) * 100 * 100) / 100;
    };

    const formatVariance = (value) => {
        if (value === null || value === undefined) {
            return 'N/A';
        }
        const formatted = formatValue(value);
        return value > 0 ? `+${formatted}` : formatted;
    };

    const actualKey = view === 'units' ? 'actualUnits' 
        : view === 'grossRev' ? 'actualGrossRev' 
        : view === 'triggers' ? 'actualTriggers'
        : view === 'overlay' ? 'actualOverlay'
        : view === 'fixedSpend' ? 'actualFixedSpend'
        : view === 'cogs' ? 'actualCogs'
        : view === 'logs' ? 'actualLogs'
        : view === 'netRev' ? 'actualNetRev'
        : view === 'grossProfit' ? 'actualGrossProfit'
        : view === 'grossProfitPercent' ? 'actualGrossProfitPercent'
        : null;

    const getActualValue = (entity, index) => {
        if (!actualKey || !entity) {
            return null;
        }
        const series = entity[actualKey];
        if (!Array.isArray(series)) {
            return null;
        }
        const value = series[index];
        return value === undefined ? null : value;
    };

    const getYtgDisplay = (
        entity,
        currentPeriod,
        ytgYear = selectedYear,
        periodNamesForYtg = rawPeriodNames,
        ytgAggregationPeriodOverride = aggregationPeriod,
        seriesOverride = null
    ) => {
        const forecastSeries = seriesOverride || entity?.[view];
        if (!Array.isArray(forecastSeries)) {
            return { value: 0, state: 'forecast' };
        }

        if (view === 'grossProfitPercent') {
            return calculateGrossProfitPercentYtgValue(entity, currentPeriod, ytgAggregationPeriodOverride, ytgYear);
        }

        if (!showActuals || !actualKey) {
            const value = calculateYTG(
                forecastSeries,
                currentPeriod,
                ytgYear,
                currentYear,
                ytgAggregationPeriodOverride,
                startMonth,
                currentCalendarMonth,
                true,
                periodNamesForYtg
            );
            return { value, state: 'forecast' };
        }

        const actualSeries = entity?.[actualKey];
        if (!Array.isArray(actualSeries)) {
            const value = calculateYTG(
                forecastSeries,
                currentPeriod,
                ytgYear,
                currentYear,
                ytgAggregationPeriodOverride,
                startMonth,
                currentCalendarMonth,
                true,
                periodNamesForYtg
            );
            return { value, state: 'forecast' };
        }

        const numericCurrentPeriod = Number(currentPeriod);
        if (!Number.isFinite(numericCurrentPeriod)) {
            return { value: 0, state: 'forecast' };
        }

        const totalPeriods = ytgAggregationPeriodOverride === 'monthly'
            ? 12
            : Math.max(0, forecastSeries.length - 1);
        const checkpointIndex = Math.max(0, Math.min(numericCurrentPeriod, Math.max(0, totalPeriods - 1)));
        const { ytg: value } = calculateYtdYtg({
            forecastSeries,
            actualSeries,
            currentPeriod,
            aggregationPeriod: ytgAggregationPeriodOverride,
            showActuals: true
        });

        const range = Array.from({ length: Math.max(0, totalPeriods - checkpointIndex) }, (_, offset) => checkpointIndex + offset);
        const hasAnyActual = range.some(idx => actualSeries[idx] !== null && actualSeries[idx] !== undefined);
        const hasAnyForecastFallback = range.some(idx => (actualSeries[idx] === null || actualSeries[idx] === undefined) && forecastSeries[idx] !== null && forecastSeries[idx] !== undefined);

        if (hasAnyActual && hasAnyForecastFallback) {
            return { value, state: ytgAggregationPeriodOverride === 'weekly' ? 'actual' : 'mixed' };
        }
        if (hasAnyActual) {
            return { value, state: 'actual' };
        }
        return { value, state: 'forecast' };
    };

    const getYtdDisplay = (
        entity,
        currentPeriod,
        ytdYear = selectedYear,
        periodNamesForYtd = ytgPeriodNames,
        ytdAggregationPeriodOverride = ytgAggregationPeriod,
        seriesOverride = null
    ) => {
        const forecastSeries = seriesOverride || entity?.[view];
        if (!Array.isArray(forecastSeries)) {
            return { value: 0, state: 'forecast' };
        }

        if (view === 'grossProfitPercent') {
            const ytg = calculateGrossProfitPercentYtgValue(entity, currentPeriod, ytdAggregationPeriodOverride, ytdYear);
            return {
                value: calculateGrossProfitPercentYtdValue(entity, currentPeriod, ytdYear),
                state: ytg.state
            };
        }

        const ytgDisplay = getYtgDisplay(
            entity,
            currentPeriod,
            ytdYear,
            periodNamesForYtd,
            ytdAggregationPeriodOverride,
            seriesOverride
        );

        if (!showActuals || !actualKey) {
            const value = calculateYTD(
                forecastSeries,
                currentPeriod,
                ytdYear,
                currentYear,
                ytdAggregationPeriodOverride,
                startMonth,
                currentCalendarMonth,
                true,
                periodNamesForYtd
            );
            return { value, state: ytgDisplay.state };
        }

        const actualSeries = entity?.[actualKey];
        if (!Array.isArray(actualSeries)) {
            const value = calculateYTD(
                forecastSeries,
                currentPeriod,
                ytdYear,
                currentYear,
                ytdAggregationPeriodOverride,
                startMonth,
                currentCalendarMonth,
                true,
                periodNamesForYtd
            );
            return { value, state: ytgDisplay.state };
        }

        const numericCurrentPeriod = Number(currentPeriod);
        if (!Number.isFinite(numericCurrentPeriod)) {
            return { value: 0, state: ytgDisplay.state };
        }

        const { ytd: value } = calculateYtdYtg({
            forecastSeries,
            actualSeries,
            currentPeriod,
            aggregationPeriod: ytdAggregationPeriodOverride,
            showActuals: true
        });

        return { value, state: ytgDisplay.state };
    };

    const getComparisonEntity = (entityType, entityIdentifier) => {
        if (!isComparingForecasts || !comparisonData) {
            return null;
        }

        if (entityType === 'totals') {
            return comparisonData.totals;
        }
        if (entityType === 'sku') {
            return comparisonData.skus?.find(s => s.skuNumber === entityIdentifier) || null;
        }
        if (entityType === 'customer') {
            return comparisonData.customers?.find(c => c.name === entityIdentifier) || null;
        }
        if (entityType === 'channel') {
            return findChannel(comparisonData.channels, entityIdentifier);
        }
        if (entityType === 'skuCustomer') {
            const [skuNumber, customerName] = entityIdentifier.split('|');
            const sku = comparisonData.skus?.find(s => s.skuNumber === skuNumber);
            return sku?.customers?.find(c => c.name === customerName) || null;
        }
        if (entityType === 'customerSku') {
            const [customerName, skuNumber] = entityIdentifier.split('|');
            const customer = comparisonData.customers?.find(c => c.name === customerName);
            return customer?.skus?.find(s => s.skuNumber === skuNumber) || null;
        }
        if (entityType === 'channelCustomer') {
            const [channelId, customerId] = entityIdentifier.split('|');
            const channel = findChannel(comparisonData.channels, channelId);
            return channel?.customers?.find(c => customerIdentifier(c) === customerId) || null;
        }
        if (entityType === 'channelCustomerSku') {
            const [channelId, customerId, productId] = entityIdentifier.split('|');
            const channel = findChannel(comparisonData.channels, channelId);
            const customer = channel?.customers?.find(c => customerIdentifier(c) === customerId);
            return customer?.skus?.find(s => skuIdentifier(s) === productId) || null;
        }
        if (entityType === 'item') {
            const { customerName, skuNumber, description } = entityIdentifier || {};
            return comparisonData.items?.find(item =>
                item.customerName === customerName &&
                item.skuNumber === skuNumber &&
                item.description === description) || null;
        }

        return null;
    };

    const getYtgSourceEntity = (entityType, entityIdentifier, useComparisonSource = false) => {
        const sourceData = useComparisonSource ? comparisonYtgSourceData : ytgSourceData;
        if (!sourceData) {
            return null;
        }

        if (entityType === 'totals') {
            return sourceData.totals;
        }
        if (entityType === 'sku') {
            return sourceData.skus?.find(s => s.skuNumber === entityIdentifier) || null;
        }
        if (entityType === 'customer') {
            return sourceData.customers?.find(c => c.name === entityIdentifier) || null;
        }
        if (entityType === 'channel') {
            return findChannel(sourceData.channels, entityIdentifier);
        }
        if (entityType === 'skuCustomer') {
            const [skuNumber, customerName] = entityIdentifier.split('|');
            const sku = sourceData.skus?.find(s => s.skuNumber === skuNumber);
            return sku?.customers?.find(c => c.name === customerName) || null;
        }
        if (entityType === 'customerSku') {
            const [customerName, skuNumber] = entityIdentifier.split('|');
            const customer = sourceData.customers?.find(c => c.name === customerName);
            return customer?.skus?.find(s => s.skuNumber === skuNumber) || null;
        }
        if (entityType === 'channelCustomer') {
            const [channelId, customerId] = entityIdentifier.split('|');
            const channel = findChannel(sourceData.channels, channelId);
            return channel?.customers?.find(c => customerIdentifier(c) === customerId) || null;
        }
        if (entityType === 'channelCustomerSku') {
            const [channelId, customerId, productId] = entityIdentifier.split('|');
            const channel = findChannel(sourceData.channels, channelId);
            const customer = channel?.customers?.find(c => customerIdentifier(c) === customerId);
            return customer?.skus?.find(s => skuIdentifier(s) === productId) || null;
        }
        if (entityType === 'item') {
            const { customerName, skuNumber, description } = entityIdentifier || {};
            return sourceData.items?.find(item =>
                item.customerName === customerName &&
                item.skuNumber === skuNumber &&
                item.description === description) || null;
        }

        return null;
    };

    const getEntitySeries = (_entityType, _entityIdentifier, entity) => {
        return entity?.[view] || [];
    };

    const getComparisonValue = (entityType, entityIdentifier, index, periodNameOverride = null) => {
        if (!isComparingForecasts || !comparisonData) {
            return null;
        }
        const comparisonEntity = getComparisonEntity(entityType, entityIdentifier);

        if (!comparisonEntity) {
            return null;
        }

        const series = comparisonEntity[view];
        if (!Array.isArray(series)) {
            return null;
        }

        const displayPeriodName = periodNameOverride || basePeriodNames[index];
        if (displayPeriodName === 'YTG') {
            const comparisonYtgEntity = getYtgSourceEntity(entityType, entityIdentifier, true) || comparisonEntity;
            const comparisonYtgCurrentPeriod = comparisonYtgSourceData?.currentMonth ?? comparisonData.currentMonth;
            const comparisonYtgPeriodNames = comparisonYtgSourceData?.periodNames || comparisonData.periodNames || rawPeriodNames;
            return getYtgDisplay(
                comparisonYtgEntity,
                comparisonYtgCurrentPeriod,
                comparisonYear,
                comparisonYtgPeriodNames,
                comparisonYtgAggregationPeriod
            );
        }

        const mainDataPeriodNames = (summaryData?.periodNames || rawPeriodNames).filter(name => name !== 'YTG');
        const comparisonPeriodNames = (comparisonData.periodNames || []).filter(name => name !== 'YTG');
        const mainPeriodName = mainDataPeriodNames[index] || (index === series.length - 1 ? 'FY' : null);
        
        if (!mainPeriodName || comparisonPeriodNames.length === 0) {
            const value = series[index];
            return value === undefined ? null : value;
        }

        const comparisonIndex = findComparisonPeriodIndex(comparisonPeriodNames, mainPeriodName, index);
        if (comparisonIndex < 0) {
            return null;
        }

        const forecastValue = series[comparisonIndex];
        const value = forecastValue === undefined ? null : forecastValue;
        return value === null ? null : { value, state: 'forecast' };
    };

    const renderComparisonLayout = (topValue, mainValue, variance, isLoading, topState = 'forecast', mainState = 'forecast') => {
        const varianceDisplay = isLoading
            ? '…'
            : variance !== null
                ? formatVariance(variance)
                : '—';
        
        const varianceColor = variance === null || isLoading
            ? 'text.secondary'
            : variance > 0
                ? 'success.main'
                : variance < 0
                    ? 'error.main'
                    : 'text.secondary';

        const resolveTypographyStyle = (state, baseVariant) => {
            if (state === 'actual') {
                return {
                    color: 'primary.main',
                    fontWeight: 700
                };
            }
            if (state === 'mixed') {
                return {
                    color: (theme) => theme.palette.mode === 'dark' ? '#7fd5ff' : '#005a9e',
                    fontWeight: 700,
                    px: 0.5,
                    borderRadius: 0.75,
                    border: '1px solid',
                    borderColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(127, 213, 255, 0.45)' : 'rgba(0, 90, 158, 0.35)',
                    backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(127, 213, 255, 0.12)' : 'rgba(0, 90, 158, 0.1)'
                };
            }
            return baseVariant === 'body2'
                ? { color: 'text.primary', fontWeight: 500 }
                : { color: 'text.secondary', fontWeight: 'normal' };
        };

        const shouldShowMixedTooltip = (state) => state === 'mixed';
        const renderStateTypography = (value, state, baseVariant) => {
            const typography = (
                <Typography
                    variant={baseVariant}
                    component="span"
                    sx={{
                        display: 'block',
                        whiteSpace: 'nowrap',
                        fontSize: baseVariant === 'caption' ? '0.9rem' : undefined,
                        ...resolveTypographyStyle(state, baseVariant)
                    }}
                >
                    {value}
                </Typography>
            );

            if (!shouldShowMixedTooltip(state)) {
                return typography;
            }

            return (
                <Tooltip title="Includes both actuals and forecast values for this period." arrow>
                    <Box component="span" sx={{ display: 'inline-block' }}>
                        {typography}
                    </Box>
                </Tooltip>
            );
        };

        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', lineHeight: 1.2 }}>
                {renderStateTypography(topValue, topState, 'caption')}
                {renderStateTypography(mainValue, mainState, 'body2')}
                <Typography
                    variant="caption"
                    component="span"
                    sx={{ display: 'block', whiteSpace: 'nowrap', color: varianceColor }}
                >
                    {varianceDisplay}
                </Typography>
            </Box>
        );
    };

    const renderValueCell = (forecastValue, actualValue, comparisonValue = null, periodIndex = null, stateOverride = null) => {
        const forecastIsNumber = typeof forecastValue === 'number' && !Number.isNaN(forecastValue);
        const isActualWindowPeriod = periodIndex !== null && isPeriodInActualsWindow(periodIndex, summaryData?.currentMonth);
        const isFy = periodIndex !== null && periodIndex === fyIndex;
        const hasActual = actualValue !== null && actualValue !== undefined;
        const isCompletedFy = isFy && !showYTG;
        const resolveState = () => {
            if (stateOverride) {
                return stateOverride;
            }
            if (!hasActual) {
                return ((isActualWindowPeriod || isCompletedFy) ? 'actual' : 'forecast');
            }
            return (isCompletedFy ? 'actual' : (isFy || !isActualWindowPeriod ? 'mixed' : 'actual'));
        };

        if (isComparingForecasts && comparisonValue !== undefined) {
            const comparisonPayload = typeof comparisonValue === 'object' && comparisonValue !== null
                ? comparisonValue
                : { value: comparisonValue, state: 'forecast' };
            const comparisonNumeric = comparisonPayload?.value;
            const hasComparison = comparisonNumeric !== null && comparisonNumeric !== undefined;
            const mainNumeric = forecastValue;
            const variance = hasComparison && typeof mainNumeric === 'number'
                ? mainNumeric - comparisonNumeric
                : null;
            
            const comparisonDisplay = loadingComparison
                ? '…'
                : hasComparison
                    ? formatValue(comparisonNumeric)
                    : '—';
            const mainDisplay = mainNumeric === null || mainNumeric === undefined
                ? '—'
                : formatValue(mainNumeric);
            const mainState = resolveState();

            return renderComparisonLayout(
                comparisonDisplay,
                mainDisplay,
                variance,
                loadingComparison,
                comparisonPayload?.state || 'forecast',
                mainState
            );
        }

        if (!showActuals || !actualKey) {
            return formatValue(forecastValue);
        }

        const variance = hasActual && forecastIsNumber ? actualValue - forecastValue : null;

        if (!showCompare) {
            const displayValue = hasActual ? actualValue : (isActualWindowPeriod ? null : forecastValue);
            const state = resolveState();
            const shouldShowMixedTooltip = state === 'mixed';
            const content = (
                <Typography
                    variant="body2"
                    sx={{
                        color: state === 'actual'
                            ? 'primary.main'
                            : state === 'mixed'
                                ? (theme) => theme.palette.mode === 'dark' ? '#7fd5ff' : '#005a9e'
                                : 'text.primary',
                        fontWeight: state === 'actual' || state === 'mixed' ? 700 : 500,
                        px: state === 'mixed' ? 0.5 : 0,
                        borderRadius: state === 'mixed' ? 0.75 : 0,
                        border: state === 'mixed' ? '1px solid' : 'none',
                        borderColor: state === 'mixed'
                            ? (theme) => theme.palette.mode === 'dark' ? 'rgba(127, 213, 255, 0.45)' : 'rgba(0, 90, 158, 0.35)'
                            : 'transparent',
                        backgroundColor: state === 'mixed'
                            ? (theme) => theme.palette.mode === 'dark' ? 'rgba(127, 213, 255, 0.12)' : 'rgba(0, 90, 158, 0.1)'
                            : 'transparent',
                        display: 'inline-block'
                    }}
                >
                    {loadingActuals ? '…' : (displayValue !== null ? formatValue(displayValue) : '—')}
                </Typography>
            );

            if (!shouldShowMixedTooltip) {
                return content;
            }

            return (
                <Tooltip title="Includes both actuals and forecast values for this period." arrow>
                    <Box component="span" sx={{ display: 'inline-block' }}>
                        {content}
                    </Box>
                </Tooltip>
            );
        }

        const actualDisplay = loadingActuals
            ? '…'
            : hasActual
                ? formatValue(actualValue)
                : '—';

        return renderComparisonLayout(
            formatValue(forecastValue),
            actualDisplay,
            variance,
            loadingActuals,
            'forecast',
            resolveState()
        );
    };

    const handleRowExpand = (rowId) => {
        setExpandedRows(prev => {
            const newSet = new Set(prev);
            if (newSet.has(rowId)) {
                newSet.delete(rowId);
                if (groupBy === 'customer') {
                    setExpandedCustomers(prevCustomers => {
                        const newCustomerSet = new Set(prevCustomers);
                        const channel = allSummaryChannels.find(
                            candidate => channelIdentifier(candidate) === rowId
                        );
                        channel?.customers?.forEach(customer => {
                            newCustomerSet.delete(`${rowId}|${customerIdentifier(customer)}`);
                        });
                        return newCustomerSet;
                    });
                }
            } else {
                newSet.add(rowId);
            }
            return newSet;
        });
    };

    const handleCustomerExpand = (channelId, customerId) => {
        setExpandedCustomers(prev => {
            const newSet = new Set(prev);
            const key = `${channelId}|${customerId}`;
            if (newSet.has(key)) {
                newSet.delete(key);
            } else {
                newSet.add(key);
            }
            return newSet;
        });
    };

    const copyToClipboard = async () => {
        await copySummaryTableToClipboard(summaryData, view, startMonth, selectedYear, currentYear, aggregationPeriod, showActuals, groupBy, expandedRows, expandedCustomers, ytgSourceData || undefined);
    };

    const expandAll = () => {
        if (groupBy === 'sku') {
            const allSkuNumbers = new Set(summaryData.skus?.map(sku => sku.skuNumber) || []);
            setExpandedRows(allSkuNumbers);
        } else if (groupBy === 'customer') {
            const allChannelIds = new Set(allSummaryChannels.map(channelIdentifier));
            setExpandedRows(allChannelIds);
            
            const allCustomerKeys = new Set();
            allSummaryChannels.forEach(channel => {
                channel.customers?.forEach(customer => {
                    allCustomerKeys.add(
                        `${channelIdentifier(channel)}|${customerIdentifier(customer)}`
                    );
                });
            });
            setExpandedCustomers(allCustomerKeys);
        }
    };

    const collapseAll = () => {
        setExpandedRows(new Set());
        setExpandedCustomers(new Set());
    };

    const hasCollapsedRows = () => {
        if (groupBy === 'sku') {
            const totalSkus = summaryData.skus?.length || 0;
            return totalSkus > expandedRows.size;
        } else if (groupBy === 'customer') {
            const totalChannels = allSummaryChannels.length;
            const totalCustomers = allSummaryChannels.reduce((sum, channel) =>
                sum + (channel.customers?.length || 0), 0) || 0;
            return totalChannels > expandedRows.size || totalCustomers > expandedCustomers.size;
        }
        return false;
    };

    const hasExpandedRows = () => {
        return expandedRows.size > 0 || expandedCustomers.size > 0;
    };

    const dataRowHoverStyle = {
        '&:hover > *': {
            backgroundColor: (theme) => theme.palette.mode === 'dark'
                ? 'rgba(255, 255, 255, 0.08)'
                : 'rgba(0, 0, 0, 0.04)'
        },
        '&:hover > th, &:hover > .sticky-cell': {
            backgroundColor: (theme) => theme.palette.mode === 'dark'
                ? 'rgba(30, 30, 30, 1) !important'
                : 'rgba(245, 245, 245, 1) !important'
        }
    };

    // ✅ Early return if summaryData is missing or malformed
    if (!summaryData || !summaryData.totals) {
        return <div>No data available</div>;
    }

    // Calculate number of periods (excluding FY total)
    const periodCount = view === 'overlay'
        ? (aggregationPeriod === 'monthly' ? 12 : (aggregationPeriod === 'quarterly' ? 4 : ((summaryData.totals?.grossRev || []).length - 1)))
        : (summaryData.totals?.[view] || []).length - 1;
    const fyIndex = periodCount;
    const totalsSeries = summaryData.totals?.[view] || [];
    const totalsYtgSeries = (getYtgSourceEntity('totals', null) || summaryData.totals)?.[view] || [];

    const stickyColumnCount = groupBy === 'list' ? 4 : 1;

    const getColumnWidth = (columnIndex) => {
        if (groupBy === 'list') {
            if (columnIndex === 0) return 50;
            if (columnIndex === 1) return 200;
            if (columnIndex === 2) return 120;
            if (columnIndex === 3) return 250;
        }
        return 'auto';
    };

    const getColumnLeft = (columnIndex) => {
        if (groupBy === 'list') {
            if (columnIndex === 0) return 0;
            if (columnIndex === 1) return 50;
            if (columnIndex === 2) return 250;
            if (columnIndex === 3) return 370;
        }
        return 0;
    };

    const getStickyColumnStyle = (columnIndex, customBgColor) => {
        const style = {
            width: getColumnWidth(columnIndex),
            minWidth: getColumnWidth(columnIndex)
        };
        
        if (stickyColumnsEnabled) {
            style.position = 'sticky';
            style.left = getColumnLeft(columnIndex);
            style.zIndex = 2;
            style.boxShadow = columnIndex === stickyColumnCount - 1 ? '2px 0 4px rgba(0,0,0,0.1)' : 'none';
        }
        
        if (customBgColor) {
            style.backgroundColor = customBgColor;
        } else {
            style.backgroundColor = (theme) => theme.palette.background.paper;
        }
        
        return style;
    };

    return (
        <TableContainer 
            component={Paper} 
            sx={{ 
                height: '100%',
                overflow: 'auto',
                maxWidth: '100%',
                display: 'inline-block',
                width: 'fit-content',
                minWidth: '100%'
            }}
        >
            <Table aria-label="account summary table" stickyHeader sx={{ 
                tableLayout: 'auto', 
                width: 'auto',
                '& .MuiTableBody-root .MuiTableCell-root': {
                    whiteSpace: 'nowrap'
                },
                '& .MuiTableHead-root .MuiTableCell-root': {
                    whiteSpace: 'nowrap'
                },
                '& .MuiTableHead-root .MuiTableCell-root[data-week-header="true"]': {
                    whiteSpace: 'pre'
                }
            }}>
                <TableHead>
                    <TableRow sx={{ '& > *': { fontWeight: 'bold' } }}>
                        <TableCell sx={{ ...getStickyColumnStyle(0), zIndex: stickyColumnsEnabled ? 3 : 'auto' }}>
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                {onToggleFullscreen && (
                                    <Tooltip title={isFullscreen ? "Exit fullscreen" : "Fullscreen"}>
                                        <IconButton
                                            onClick={onToggleFullscreen}
                                            size="small"
                                            sx={{ p: 0.5 }}
                                        >
                                            {isFullscreen ? <FullscreenExitIcon fontSize="small" /> : <FullscreenIcon fontSize="small" />}
                                        </IconButton>
                                    </Tooltip>
                                )}
                                <Tooltip title={stickyColumnsEnabled ? "Unpin columns" : "Pin columns"}>
                                    <IconButton
                                        onClick={() => setStickyColumnsEnabled(prev => !prev)}
                                        size="small"
                                        sx={{ p: 0.5 }}
                                    >
                                        {stickyColumnsEnabled ? <PushPinIcon fontSize="small" /> : <PushPinOutlinedIcon fontSize="small" />}
                                    </IconButton>
                                </Tooltip>
                                <Tooltip title="Copy table to clipboard">
                                    <IconButton
                                        onClick={copyToClipboard}
                                        size="small"
                                        sx={{ p: 0.5 }}
                                    >
                                        <ContentCopyIcon fontSize="small" />
                                    </IconButton>
                                </Tooltip>
                                {(groupBy === 'sku' || groupBy === 'customer') && hasCollapsedRows() && (
                                    <Tooltip title="Expand all">
                                        <IconButton
                                            onClick={expandAll}
                                            size="small"
                                            sx={{ p: 0.5 }}
                                        >
                                            <UnfoldMoreIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                )}
                                {(groupBy === 'sku' || groupBy === 'customer') && hasExpandedRows() && (
                                    <Tooltip title="Collapse all">
                                        <IconButton
                                            onClick={collapseAll}
                                            size="small"
                                            sx={{ p: 0.5 }}
                                        >
                                            <UnfoldLessIcon fontSize="small" />
                                        </IconButton>
                                    </Tooltip>
                                )}
                            </Box>
                        </TableCell>
                        {groupBy === 'list' && (
                            <>
                                <TableCell sx={{ ...getStickyColumnStyle(1), zIndex: stickyColumnsEnabled ? 3 : 'auto' }}>Customer</TableCell>
                                <TableCell sx={{ ...getStickyColumnStyle(2), zIndex: stickyColumnsEnabled ? 3 : 'auto' }}>SKU</TableCell>
                                <TableCell sx={{ ...getStickyColumnStyle(3), zIndex: stickyColumnsEnabled ? 3 : 'auto' }}>Description</TableCell>
                            </>
                        )}
                        {periodNames.map((period, index) => {
                            const isWeekHeader = aggregationPeriod === "weekly" && isDatePeriod(period);
                            const isYtgOrFy = period === 'YTD' || period === 'YTG' || period === 'FY';
                            return (
                                <TableCell 
                                    key={`${period}-${index}`} 
                                    align="right" 
                                    sx={{ 
                                        fontWeight: isYtgOrFy ? 'bold' : 'normal',
                                        whiteSpace: isWeekHeader ? 'pre' : 'nowrap',
                                        backgroundColor: (theme) => theme.palette.background.paper
                                    }}
                                    data-week-header={isWeekHeader}
                                >
                                    {period.toUpperCase()}
                                </TableCell>
                            );
                        })}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {/* ✅ TOTALS Row (from API) */}
                    {groupBy === 'list' ? (
                        <TableRow sx={{ '& > *': { fontWeight: 'bold' } }}>
                            <TableCell sx={getStickyColumnStyle(0)}></TableCell>
                            <TableCell component="th" scope="row" sx={getStickyColumnStyle(1)}>TOTAL</TableCell>
                            <TableCell sx={getStickyColumnStyle(2)}></TableCell>
                            <TableCell sx={getStickyColumnStyle(3)}></TableCell>
                            {(totalsSeries || []).slice(0, periodCount).map((total, index) => (
                                <TableCell align="right" key={index}>
                                    {renderValueCell(
                                        total, 
                                        getActualValue(summaryData.totals, index),
                                        getComparisonValue('totals', null, index),
                                        index
                                    )}
                                </TableCell>
                            ))}
                            {showYTG && (
                                <TableCell align="right">
                                    {renderValueCell(
                                        getYtdDisplay(getYtgSourceEntity('totals', null) || summaryData.totals, ytgCurrentPeriod, selectedYear, ytgPeriodNames, ytgAggregationPeriod, totalsYtgSeries).value,
                                        null,
                                        getYtdDisplay(getYtgSourceEntity('totals', null, true) || getComparisonEntity('totals', null), comparisonYtgCurrentPeriod, comparisonYear, comparisonYtgPeriodNames, comparisonYtgAggregationPeriod).value,
                                        null,
                                        getYtgDisplay(getYtgSourceEntity('totals', null) || summaryData.totals, ytgCurrentPeriod, selectedYear, ytgPeriodNames, ytgAggregationPeriod, totalsYtgSeries).state
                                    )}
                                </TableCell>
                            )}
                            {showYTG && (
                                <TableCell align="right">
                                    {renderValueCell(
                                        getYtgDisplay(getYtgSourceEntity('totals', null) || summaryData.totals, ytgCurrentPeriod, selectedYear, ytgPeriodNames, ytgAggregationPeriod, totalsYtgSeries).value,
                                        null,
                                        getComparisonValue('totals', null, periodCount, 'YTG'),
                                        null,
                                        getYtgDisplay(getYtgSourceEntity('totals', null) || summaryData.totals, ytgCurrentPeriod, selectedYear, ytgPeriodNames, ytgAggregationPeriod, totalsYtgSeries).state
                                    )}
                                </TableCell>
                            )}
                            <TableCell align="right">
                                {renderValueCell(
                                    (totalsSeries || [])[fyIndex],
                                    getActualValue(summaryData.totals, fyIndex),
                                    getComparisonValue('totals', null, fyIndex, 'FY'),
                                    fyIndex
                                )}
                            </TableCell>
                        </TableRow>
                    ) : (
                        <TableRow sx={{ '& > *': { fontWeight: 'bold' } }}>
                            <TableCell component="th" scope="row" sx={getStickyColumnStyle(0)}>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    {(groupBy === 'sku' || groupBy === 'customer') && <Box sx={{ width: 24 }} />}
                                    TOTAL
                                </Box>
                            </TableCell>
                            {(totalsSeries || []).slice(0, periodCount).map((total, index) => (
                                <TableCell align="right" key={index}>
                                    {renderValueCell(
                                        total, 
                                        getActualValue(summaryData.totals, index),
                                        getComparisonValue('totals', null, index),
                                        index
                                    )}
                                </TableCell>
                            ))}
                            {showYTG && (
                                <TableCell align="right">
                                    {renderValueCell(
                                        getYtdDisplay(getYtgSourceEntity('totals', null) || summaryData.totals, ytgCurrentPeriod, selectedYear, ytgPeriodNames, ytgAggregationPeriod, totalsYtgSeries).value,
                                        null,
                                        getYtdDisplay(getYtgSourceEntity('totals', null, true) || getComparisonEntity('totals', null), comparisonYtgCurrentPeriod, comparisonYear, comparisonYtgPeriodNames, comparisonYtgAggregationPeriod).value,
                                        null,
                                        getYtgDisplay(getYtgSourceEntity('totals', null) || summaryData.totals, ytgCurrentPeriod, selectedYear, ytgPeriodNames, ytgAggregationPeriod, totalsYtgSeries).state
                                    )}
                                </TableCell>
                            )}
                            {showYTG && (
                                <TableCell align="right">
                                    {renderValueCell(
                                        getYtgDisplay(getYtgSourceEntity('totals', null) || summaryData.totals, ytgCurrentPeriod, selectedYear, ytgPeriodNames, ytgAggregationPeriod, totalsYtgSeries).value,
                                        null,
                                        getComparisonValue('totals', null, periodCount, 'YTG'),
                                        null,
                                        getYtgDisplay(getYtgSourceEntity('totals', null) || summaryData.totals, ytgCurrentPeriod, selectedYear, ytgPeriodNames, ytgAggregationPeriod, totalsYtgSeries).state
                                    )}
                                </TableCell>
                            )}
                            <TableCell align="right">
                                {renderValueCell(
                                    (totalsSeries || [])[fyIndex],
                                    getActualValue(summaryData.totals, fyIndex),
                                    getComparisonValue('totals', null, fyIndex, 'FY'),
                                    fyIndex
                                )}
                            </TableCell>
                        </TableRow>
                    )}

                    {/* SKU groupBy mode - show SKUs with expandable customers */}
                    {groupBy === 'sku' && summaryData.skus?.map((sku) => (
                        <React.Fragment key={sku.skuNumber}>
                            {/* SKU Row with expansion */}
                            <TableRow 
                                sx={{ 
                                    cursor: 'pointer',
                                    '& > *': { 
                                        backgroundColor: (theme) => theme.palette.mode === 'dark' 
                                            ? 'rgba(255, 255, 255, 0.05)' 
                                            : 'rgba(0, 0, 0, 0.04)'
                                    },
                                    '&:hover > *': {
                                        backgroundColor: (theme) => theme.palette.mode === 'dark'
                                            ? 'rgba(255, 255, 255, 0.08)'
                                            : 'rgba(0, 0, 0, 0.08)'
                                    },
                                    '&:hover > th': {
                                        backgroundColor: (theme) => theme.palette.mode === 'dark' 
                                            ? 'rgba(30, 30, 30, 1) !important' 
                                            : 'rgba(238, 238, 238, 1) !important'
                                    }
                                }}
                                onClick={() => handleRowExpand(sku.skuNumber)}
                            >
                                <TableCell component="th" scope="row" sx={{ 
                                    ...getStickyColumnStyle(0),
                                    backgroundColor: (theme) => theme.palette.mode === 'dark' 
                                        ? 'rgba(18, 18, 18, 1)' 
                                        : 'rgba(245, 245, 245, 1)'
                                }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <IconButton
                                            aria-label="expand row"
                                            size="small"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleRowExpand(sku.skuNumber);
                                            }}
                                            sx={{ p: 0 }}
                                        >
                                            {expandedRows.has(sku.skuNumber) ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
                                        </IconButton>
                                        {sku.skuNumber} - {sku.description}
                                    </Box>
                                </TableCell>
                                {getEntitySeries('sku', sku.skuNumber, sku).slice(0, periodCount).map((value, index) => (
                                    <TableCell align="right" key={index}>
                                        {renderValueCell(
                                            value, 
                                            getActualValue(sku, index),
                                            getComparisonValue('sku', sku.skuNumber, index),
                                            index
                                        )}
                                    </TableCell>
                                ))}
                                {showYTG && (
                                    <TableCell align="right">
                                        {renderValueCell(
                                            getYtdDisplay(getYtgSourceEntity('sku', sku.skuNumber) || sku, ytgCurrentPeriod, selectedYear, ytgPeriodNames, ytgAggregationPeriod, getEntitySeries('sku', sku.skuNumber, getYtgSourceEntity('sku', sku.skuNumber) || sku, ytgAggregationPeriod)).value,
                                            null,
                                            getYtdDisplay(getYtgSourceEntity('sku', sku.skuNumber, true) || getComparisonEntity('sku', sku.skuNumber), comparisonYtgCurrentPeriod, comparisonYear, comparisonYtgPeriodNames, comparisonYtgAggregationPeriod).value,
                                            null,
                                            getYtgDisplay(getYtgSourceEntity('sku', sku.skuNumber) || sku, ytgCurrentPeriod, selectedYear, ytgPeriodNames, ytgAggregationPeriod, getEntitySeries('sku', sku.skuNumber, getYtgSourceEntity('sku', sku.skuNumber) || sku, ytgAggregationPeriod)).state
                                        )}
                                    </TableCell>
                                )}
                                {showYTG && (
                                    <TableCell align="right">
                                        {renderValueCell(
                                            getYtgDisplay(getYtgSourceEntity('sku', sku.skuNumber) || sku, ytgCurrentPeriod, selectedYear, ytgPeriodNames, ytgAggregationPeriod, getEntitySeries('sku', sku.skuNumber, getYtgSourceEntity('sku', sku.skuNumber) || sku, ytgAggregationPeriod)).value,
                                            null,
                                            getComparisonValue('sku', sku.skuNumber, periodCount, 'YTG'),
                                            null,
                                            getYtgDisplay(getYtgSourceEntity('sku', sku.skuNumber) || sku, ytgCurrentPeriod, selectedYear, ytgPeriodNames, ytgAggregationPeriod, getEntitySeries('sku', sku.skuNumber, getYtgSourceEntity('sku', sku.skuNumber) || sku, ytgAggregationPeriod)).state
                                        )}
                                    </TableCell>
                                )}
                                <TableCell align="right">
                                    {renderValueCell(
                                        getEntitySeries('sku', sku.skuNumber, sku)[fyIndex],
                                        getActualValue(sku, fyIndex),
                                        getComparisonValue('sku', sku.skuNumber, fyIndex, 'FY'),
                                        fyIndex
                                    )}
                                </TableCell>
                            </TableRow>

                            {/* Customer Rows (shown when SKU is expanded) */}
                            {expandedRows.has(sku.skuNumber) && sku.customers?.map((customer) => (
                                <TableRow key={`${sku.skuNumber}-${customer.name}`} sx={dataRowHoverStyle}>
                                    <TableCell component="th" scope="row" sx={{ ...getStickyColumnStyle(0), pl: 6 }}>{customer.name}</TableCell>
                                    {getEntitySeries('skuCustomer', `${sku.skuNumber}|${customer.name}`, customer).slice(0, periodCount).map((value, index) => (
                                        <TableCell align="right" key={index}>
                                            {renderValueCell(
                                                value, 
                                                getActualValue(customer, index),
                                                getComparisonValue('skuCustomer', `${sku.skuNumber}|${customer.name}`, index),
                                                index
                                            )}
                                        </TableCell>
                                    ))}
                                    {showYTG && (
                                        <TableCell align="right">
                                            {renderValueCell(
                                                getYtdDisplay(getYtgSourceEntity('skuCustomer', `${sku.skuNumber}|${customer.name}`) || customer, ytgCurrentPeriod, selectedYear, ytgPeriodNames, ytgAggregationPeriod, getEntitySeries('skuCustomer', `${sku.skuNumber}|${customer.name}`, getYtgSourceEntity('skuCustomer', `${sku.skuNumber}|${customer.name}`) || customer, ytgAggregationPeriod)).value,
                                                null,
                                                getYtdDisplay(getYtgSourceEntity('skuCustomer', `${sku.skuNumber}|${customer.name}`, true) || getComparisonEntity('skuCustomer', `${sku.skuNumber}|${customer.name}`), comparisonYtgCurrentPeriod, comparisonYear, comparisonYtgPeriodNames, comparisonYtgAggregationPeriod).value,
                                                null,
                                                getYtgDisplay(getYtgSourceEntity('skuCustomer', `${sku.skuNumber}|${customer.name}`) || customer, ytgCurrentPeriod, selectedYear, ytgPeriodNames, ytgAggregationPeriod, getEntitySeries('skuCustomer', `${sku.skuNumber}|${customer.name}`, getYtgSourceEntity('skuCustomer', `${sku.skuNumber}|${customer.name}`) || customer, ytgAggregationPeriod)).state
                                            )}
                                    </TableCell>
                                )}
                                {showYTG && (
                                        <TableCell align="right">
                                            {renderValueCell(
                                                getYtgDisplay(getYtgSourceEntity('skuCustomer', `${sku.skuNumber}|${customer.name}`) || customer, ytgCurrentPeriod, selectedYear, ytgPeriodNames, ytgAggregationPeriod, getEntitySeries('skuCustomer', `${sku.skuNumber}|${customer.name}`, getYtgSourceEntity('skuCustomer', `${sku.skuNumber}|${customer.name}`) || customer, ytgAggregationPeriod)).value,
                                                null,
                                                getComparisonValue('skuCustomer', `${sku.skuNumber}|${customer.name}`, periodCount, 'YTG'),
                                                null,
                                                getYtgDisplay(getYtgSourceEntity('skuCustomer', `${sku.skuNumber}|${customer.name}`) || customer, ytgCurrentPeriod, selectedYear, ytgPeriodNames, ytgAggregationPeriod, getEntitySeries('skuCustomer', `${sku.skuNumber}|${customer.name}`, getYtgSourceEntity('skuCustomer', `${sku.skuNumber}|${customer.name}`) || customer, ytgAggregationPeriod)).state
                                            )}
                                        </TableCell>
                                    )}
                                    <TableCell align="right">
                                        {renderValueCell(
                                            getEntitySeries('skuCustomer', `${sku.skuNumber}|${customer.name}`, customer)[fyIndex],
                                            getActualValue(customer, fyIndex),
                                            getComparisonValue('skuCustomer', `${sku.skuNumber}|${customer.name}`, fyIndex, 'FY'),
                                            fyIndex
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </React.Fragment>
                    ))}

                    {/* Customer groupBy mode - show channels with expandable customers with expandable SKUs */}
                    {groupBy === 'customer' && visibleSummaryChannels.map((channel) => (
                        <React.Fragment key={channelIdentifier(channel)}>
                            {/* Channel Row with expansion */}
                            <TableRow 
                                sx={{ 
                                    cursor: 'pointer',
                                    '& > *': { 
                                        backgroundColor: (theme) => theme.palette.mode === 'dark' 
                                            ? 'rgba(255, 255, 255, 0.05)' 
                                            : 'rgba(0, 0, 0, 0.04)'
                                    },
                                    '&:hover > *': {
                                        backgroundColor: (theme) => theme.palette.mode === 'dark'
                                            ? 'rgba(255, 255, 255, 0.08)'
                                            : 'rgba(0, 0, 0, 0.08)'
                                    },
                                    '&:hover > th': {
                                        backgroundColor: (theme) => theme.palette.mode === 'dark' 
                                            ? 'rgba(30, 30, 30, 1) !important' 
                                            : 'rgba(238, 238, 238, 1) !important'
                                    }
                                }}
                                onClick={() => handleRowExpand(channelIdentifier(channel))}
                            >
                                <TableCell component="th" scope="row" sx={{ 
                                    ...getStickyColumnStyle(0),
                                    backgroundColor: (theme) => theme.palette.mode === 'dark' 
                                        ? 'rgba(18, 18, 18, 1)' 
                                        : 'rgba(245, 245, 245, 1)',
                                    pl: 1 + (channel.hierarchyDepth * 3)
                                }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <IconButton
                                            aria-label="expand row"
                                            size="small"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                handleRowExpand(channelIdentifier(channel));
                                            }}
                                            sx={{ p: 0 }}
                                        >
                                            {expandedRows.has(channelIdentifier(channel)) ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
                                        </IconButton>
                                        {channel.displayName}
                                    </Box>
                                </TableCell>
                                {getEntitySeries('channel', channelIdentifier(channel), channel).slice(0, periodCount).map((value, index) => (
                                    <TableCell align="right" key={index}>
                                        {renderValueCell(
                                            value, 
                                            getActualValue(channel, index),
                                            getComparisonValue('channel', channelIdentifier(channel), index),
                                            index
                                        )}
                                    </TableCell>
                                ))}
                                {showYTG && (
                                    <TableCell align="right">
                                        {renderValueCell(
                                            getYtdDisplay(getYtgSourceEntity('channel', channel.name) || channel, ytgCurrentPeriod, selectedYear, ytgPeriodNames, ytgAggregationPeriod, getEntitySeries('channel', channel.name, getYtgSourceEntity('channel', channel.name) || channel, ytgAggregationPeriod)).value,
                                            null,
                                            getYtdDisplay(getYtgSourceEntity('channel', channel.name, true) || getComparisonEntity('channel', channel.name), comparisonYtgCurrentPeriod, comparisonYear, comparisonYtgPeriodNames, comparisonYtgAggregationPeriod).value,
                                            null,
                                            getYtgDisplay(getYtgSourceEntity('channel', channel.name) || channel, ytgCurrentPeriod, selectedYear, ytgPeriodNames, ytgAggregationPeriod, getEntitySeries('channel', channel.name, getYtgSourceEntity('channel', channel.name) || channel, ytgAggregationPeriod)).state
                                        )}
                                    </TableCell>
                                )}
                                {showYTG && (
                                    <TableCell align="right">
                                        {renderValueCell(
                                            getYtgDisplay(getYtgSourceEntity('channel', channel.name) || channel, ytgCurrentPeriod, selectedYear, ytgPeriodNames, ytgAggregationPeriod, getEntitySeries('channel', channel.name, getYtgSourceEntity('channel', channel.name) || channel, ytgAggregationPeriod)).value,
                                            null,
                                            getComparisonValue('channel', channel.name, periodCount, 'YTG'),
                                            null,
                                            getYtgDisplay(getYtgSourceEntity('channel', channel.name) || channel, ytgCurrentPeriod, selectedYear, ytgPeriodNames, ytgAggregationPeriod, getEntitySeries('channel', channel.name, getYtgSourceEntity('channel', channel.name) || channel, ytgAggregationPeriod)).state
                                        )}
                                    </TableCell>
                                )}
                                <TableCell align="right">
                                    {renderValueCell(
                                        getEntitySeries('channel', channel.name, channel)[fyIndex],
                                        getActualValue(channel, fyIndex),
                                        getComparisonValue('channel', channel.name, fyIndex, 'FY'),
                                        fyIndex
                                    )}
                                </TableCell>
                            </TableRow>

                            {/* Customer Rows (shown when channel is expanded) */}
                            {expandedRows.has(channelIdentifier(channel)) && channel.customers?.map((customer) => (
                                <React.Fragment key={`${channel.name}-${customer.name}`}>
                                    <TableRow 
                                        sx={{ 
                                            cursor: 'pointer',
                                            '& > *': { 
                                                backgroundColor: (theme) => theme.palette.mode === 'dark' 
                                                    ? 'rgba(255, 255, 255, 0.02)' 
                                                    : 'rgba(0, 0, 0, 0.02)'
                                            },
                                            '&:hover > *': {
                                                backgroundColor: (theme) => theme.palette.mode === 'dark'
                                                    ? 'rgba(255, 255, 255, 0.06)'
                                                    : 'rgba(0, 0, 0, 0.06)'
                                            },
                                            '&:hover > th': {
                                                backgroundColor: (theme) => theme.palette.mode === 'dark' 
                                                    ? 'rgba(25, 25, 25, 1) !important' 
                                                    : 'rgba(245, 245, 245, 1) !important'
                                            }
                                        }}
                                        onClick={() => handleCustomerExpand(channel.name, customer.name)}
                                    >
                                        <TableCell component="th" scope="row" sx={{ 
                                            ...getStickyColumnStyle(0),
                                            backgroundColor: (theme) => theme.palette.mode === 'dark' 
                                                ? 'rgba(18, 18, 18, 1)' 
                                                : 'rgba(250, 250, 250, 1)',
                                            pl: 6 
                                        }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <IconButton
                                                    aria-label="expand customer"
                                                    size="small"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleCustomerExpand(channel.name, customer.name);
                                                    }}
                                                    sx={{ p: 0 }}
                                                >
                                                    {expandedCustomers.has(`${channel.name}|${customer.name}`) ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
                                                </IconButton>
                                                {customer.displayName}
                                            </Box>
                                        </TableCell>
                                        {getEntitySeries('channelCustomer', `${channel.name}|${customer.name}`, customer).slice(0, periodCount).map((value, index) => (
                                            <TableCell align="right" key={index}>
                                                {renderValueCell(
                                                    value, 
                                                    getActualValue(customer, index),
                                                    getComparisonValue('channelCustomer', `${channel.name}|${customer.name}`, index),
                                                    index
                                                )}
                                            </TableCell>
                                        ))}
                                        {showYTG && (
                                            <TableCell align="right">
                                                {renderValueCell(
                                                    getYtdDisplay(getYtgSourceEntity('channelCustomer', `${channel.name}|${customer.name}`) || customer, ytgCurrentPeriod, selectedYear, ytgPeriodNames, ytgAggregationPeriod, getEntitySeries('channelCustomer', `${channel.name}|${customer.name}`, getYtgSourceEntity('channelCustomer', `${channel.name}|${customer.name}`) || customer, ytgAggregationPeriod)).value,
                                                    null,
                                                    getYtdDisplay(getYtgSourceEntity('channelCustomer', `${channel.name}|${customer.name}`, true) || getComparisonEntity('channelCustomer', `${channel.name}|${customer.name}`), comparisonYtgCurrentPeriod, comparisonYear, comparisonYtgPeriodNames, comparisonYtgAggregationPeriod).value,
                                                    null,
                                                    getYtgDisplay(getYtgSourceEntity('channelCustomer', `${channel.name}|${customer.name}`) || customer, ytgCurrentPeriod, selectedYear, ytgPeriodNames, ytgAggregationPeriod, getEntitySeries('channelCustomer', `${channel.name}|${customer.name}`, getYtgSourceEntity('channelCustomer', `${channel.name}|${customer.name}`) || customer, ytgAggregationPeriod)).state
                                                )}
                                    </TableCell>
                                )}
                                {showYTG && (
                                            <TableCell align="right">
                                                {renderValueCell(
                                                    getYtgDisplay(getYtgSourceEntity('channelCustomer', `${channel.name}|${customer.name}`) || customer, ytgCurrentPeriod, selectedYear, ytgPeriodNames, ytgAggregationPeriod, getEntitySeries('channelCustomer', `${channel.name}|${customer.name}`, getYtgSourceEntity('channelCustomer', `${channel.name}|${customer.name}`) || customer, ytgAggregationPeriod)).value,
                                                    null,
                                                    getComparisonValue('channelCustomer', `${channel.name}|${customer.name}`, periodCount, 'YTG'),
                                                    null,
                                                    getYtgDisplay(getYtgSourceEntity('channelCustomer', `${channel.name}|${customer.name}`) || customer, ytgCurrentPeriod, selectedYear, ytgPeriodNames, ytgAggregationPeriod, getEntitySeries('channelCustomer', `${channel.name}|${customer.name}`, getYtgSourceEntity('channelCustomer', `${channel.name}|${customer.name}`) || customer, ytgAggregationPeriod)).state
                                                )}
                                            </TableCell>
                                        )}
                                        <TableCell align="right">
                                            {renderValueCell(
                                                getEntitySeries('channelCustomer', `${channel.name}|${customer.name}`, customer)[fyIndex],
                                                getActualValue(customer, fyIndex),
                                                getComparisonValue('channelCustomer', `${channel.name}|${customer.name}`, fyIndex, 'FY'),
                                                fyIndex
                                            )}
                                        </TableCell>
                                    </TableRow>

                                    {/* SKU Rows (shown when customer is expanded) */}
                                    {expandedCustomers.has(`${channel.name}|${customer.name}`) && customer.skus?.map((sku) => (
                                        <TableRow key={`${channel.name}-${customer.name}-${sku.skuNumber}`} sx={dataRowHoverStyle}>
                                            <TableCell component="th" scope="row" sx={{ ...getStickyColumnStyle(0), pl: 10 }}>
                                                {sku.displaySkuNumber} - {sku.description}
                                            </TableCell>
                                            {getEntitySeries('channelCustomerSku', `${channel.name}|${customer.name}|${sku.skuNumber}`, sku).slice(0, periodCount).map((value, index) => (
                                                <TableCell align="right" key={index}>
                                                    {renderValueCell(
                                                        value, 
                                                        getActualValue(sku, index),
                                                        getComparisonValue('channelCustomerSku', `${channel.name}|${customer.name}|${sku.skuNumber}`, index),
                                                        index
                                                    )}
                                                </TableCell>
                                            ))}
                                            {showYTG && (
                                                <TableCell align="right">
                                                    {renderValueCell(
                                                        getYtdDisplay(getYtgSourceEntity('channelCustomerSku', `${channel.name}|${customer.name}|${sku.skuNumber}`) || sku, ytgCurrentPeriod, selectedYear, ytgPeriodNames, ytgAggregationPeriod, getEntitySeries('channelCustomerSku', `${channel.name}|${customer.name}|${sku.skuNumber}`, getYtgSourceEntity('channelCustomerSku', `${channel.name}|${customer.name}|${sku.skuNumber}`) || sku, ytgAggregationPeriod)).value,
                                                        null,
                                                        getYtdDisplay(getYtgSourceEntity('channelCustomerSku', `${channel.name}|${customer.name}|${sku.skuNumber}`, true) || getComparisonEntity('channelCustomerSku', `${channel.name}|${customer.name}|${sku.skuNumber}`), comparisonYtgCurrentPeriod, comparisonYear, comparisonYtgPeriodNames, comparisonYtgAggregationPeriod).value,
                                                        null,
                                                        getYtgDisplay(getYtgSourceEntity('channelCustomerSku', `${channel.name}|${customer.name}|${sku.skuNumber}`) || sku, ytgCurrentPeriod, selectedYear, ytgPeriodNames, ytgAggregationPeriod, getEntitySeries('channelCustomerSku', `${channel.name}|${customer.name}|${sku.skuNumber}`, getYtgSourceEntity('channelCustomerSku', `${channel.name}|${customer.name}|${sku.skuNumber}`) || sku, ytgAggregationPeriod)).state
                                                    )}
                                    </TableCell>
                                )}
                                {showYTG && (
                                                <TableCell align="right">
                                                    {renderValueCell(
                                                        getYtgDisplay(getYtgSourceEntity('channelCustomerSku', `${channel.name}|${customer.name}|${sku.skuNumber}`) || sku, ytgCurrentPeriod, selectedYear, ytgPeriodNames, ytgAggregationPeriod, getEntitySeries('channelCustomerSku', `${channel.name}|${customer.name}|${sku.skuNumber}`, getYtgSourceEntity('channelCustomerSku', `${channel.name}|${customer.name}|${sku.skuNumber}`) || sku, ytgAggregationPeriod)).value,
                                                        null,
                                                        getComparisonValue('channelCustomerSku', `${channel.name}|${customer.name}|${sku.skuNumber}`, periodCount, 'YTG'),
                                                        null,
                                                        getYtgDisplay(getYtgSourceEntity('channelCustomerSku', `${channel.name}|${customer.name}|${sku.skuNumber}`) || sku, ytgCurrentPeriod, selectedYear, ytgPeriodNames, ytgAggregationPeriod, getEntitySeries('channelCustomerSku', `${channel.name}|${customer.name}|${sku.skuNumber}`, getYtgSourceEntity('channelCustomerSku', `${channel.name}|${customer.name}|${sku.skuNumber}`) || sku, ytgAggregationPeriod)).state
                                                    )}
                                                </TableCell>
                                            )}
                                            <TableCell align="right">
                                                {renderValueCell(
                                                    getEntitySeries('channelCustomerSku', `${channel.name}|${customer.name}|${sku.skuNumber}`, sku)[fyIndex],
                                                    getActualValue(sku, fyIndex),
                                                    getComparisonValue('channelCustomerSku', `${channel.name}|${customer.name}|${sku.skuNumber}`, fyIndex, 'FY'),
                                                    fyIndex
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                </React.Fragment>
                            ))}
                        </React.Fragment>
                    ))}

                    {/* List mode (formerly channel) - flat table with Customer/SKU/Description columns */}
                    {groupBy === 'list' && summaryData.items?.map((item, itemIndex) => (
                        <TableRow key={`list-${itemIndex}`} sx={dataRowHoverStyle}>
                            <TableCell className="sticky-cell" sx={getStickyColumnStyle(0)} />
                            <TableCell component="th" scope="row" className="sticky-cell" sx={getStickyColumnStyle(1)}>{item.customerName}</TableCell>
                            <TableCell className="sticky-cell" sx={getStickyColumnStyle(2)}>{item.skuNumber}</TableCell>
                            <TableCell className="sticky-cell" sx={getStickyColumnStyle(3)}>{item.description}</TableCell>
                            {getEntitySeries('item', item, item).slice(0, periodCount).map((value, index) => (
                                <TableCell align="right" key={index}>
                                    {renderValueCell(
                                        value, 
                                        getActualValue(item, index),
                                        getComparisonValue('item', item, index),
                                        index
                                    )}
                                </TableCell>
                            ))}
                            {showYTG && (
                                <TableCell align="right">
                                    {renderValueCell(
                                        getYtdDisplay(getYtgSourceEntity('item', item) || item, ytgCurrentPeriod, selectedYear, ytgPeriodNames, ytgAggregationPeriod, getEntitySeries('item', item, getYtgSourceEntity('item', item) || item, ytgAggregationPeriod)).value,
                                        null,
                                        getYtdDisplay(getYtgSourceEntity('item', item, true) || getComparisonEntity('item', item), comparisonYtgCurrentPeriod, comparisonYear, comparisonYtgPeriodNames, comparisonYtgAggregationPeriod).value,
                                        null,
                                        getYtgDisplay(getYtgSourceEntity('item', item) || item, ytgCurrentPeriod, selectedYear, ytgPeriodNames, ytgAggregationPeriod, getEntitySeries('item', item, getYtgSourceEntity('item', item) || item, ytgAggregationPeriod)).state
                                    )}
                                </TableCell>
                            )}
                            {showYTG && (
                                <TableCell align="right">
                                    {renderValueCell(
                                        getYtgDisplay(getYtgSourceEntity('item', item) || item, ytgCurrentPeriod, selectedYear, ytgPeriodNames, ytgAggregationPeriod, getEntitySeries('item', item, getYtgSourceEntity('item', item) || item, ytgAggregationPeriod)).value,
                                        null,
                                        getComparisonValue('item', item, periodCount, 'YTG'),
                                        null,
                                        getYtgDisplay(getYtgSourceEntity('item', item) || item, ytgCurrentPeriod, selectedYear, ytgPeriodNames, ytgAggregationPeriod, getEntitySeries('item', item, getYtgSourceEntity('item', item) || item, ytgAggregationPeriod)).state
                                    )}
                                </TableCell>
                            )}
                            <TableCell align="right">
                                {renderValueCell(
                                    getEntitySeries('item', item, item)[fyIndex],
                                    getActualValue(item, fyIndex),
                                    getComparisonValue('item', item, fyIndex, 'FY'),
                                    fyIndex
                                )}
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </TableContainer>
    );
};

export default OutputsSummaryTable;
